import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Paperclip, Trash2, Upload } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/features/common/ui';
import { formatBytes, type AttachableType } from '@fint/validation';

interface Attachment {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  originalSizeBytes: number | null;
  savedPercent: number;
  isImage: boolean;
  createdAt: string;
  /**
   * 'source' marks the original document the record was MADE from - the old
   * system's invoice PDF, the supplier's bill - as distinct from a receipt
   * attached afterwards (migration 0018). Optional on the wire; a list that
   * does not carry it reads as it did.
   */
  role?: 'source' | null;
}

/**
 * The source first, then the rest as they came.
 *
 * A stable sort, so two receipts keep the order the server gave them; the
 * source moves to the top because it is the document the record IS - a
 * person checking an imported invoice against Zoho's PDF should not have to
 * find it under three signed copies.
 */
export function sourceFirst<T extends { role?: 'source' | null }>(files: T[]): T[] {
  return [...files].sort((a, b) => Number(b.role === 'source') - Number(a.role === 'source'));
}

/**
 * The receipts strip.
 *
 * ── WHY DROP AND PASTE, NOT JUST A BUTTON ──
 * The two files this feature exists for arrive differently. A receipt is
 * photographed and dragged in from a folder; a bank screenshot is taken with a
 * key combination and is sitting on the clipboard, never saved anywhere. A
 * paste handler is the difference between two clicks and a trip through the
 * Downloads folder to find `Screenshot 2026-09-01 at 14.22.13.png`.
 *
 * ── WHY DOWNLOADS ARE A PLAIN LINK ──
 * `<a download>` is a browser navigation, and a navigation cannot carry the
 * `x-org-id` header the rest of the app sends - which is why the route takes
 * the org in its path. Fetching the file as a blob instead would work and would
 * also mean holding a whole PDF in memory to hand it straight back to the
 * browser. See docs/coding-standards.md, "A download cannot carry a header".
 */
export function Attachments({
  ownerType,
  ownerId,
  title = 'Attachments',
  emptyHint,
  uploadAs,
}: {
  ownerType: AttachableType;
  ownerId: string;
  title?: string;
  emptyHint?: string;
  /**
   * 'source' when the next file IS the record's original - an imported
   * invoice that has none yet. The button says so, and the upload carries
   * the role, so the PDF lands marked rather than as one more attachment.
   */
  uploadAs?: 'source';
}) {
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const key = ['attachments', org?.id, ownerType, ownerId];

  const query = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ items: Attachment[] }>(`/files?ownerType=${ownerType}&ownerId=${ownerId}`),
    enabled: !!org?.id && !!ownerId && perms.can('attachment', 'view'),
  });

  const upload = useMutation({
    mutationFn: (file: File) =>
      api.upload<Attachment>(`/files?ownerType=${ownerType}&ownerId=${ownerId}${uploadAs ? `&role=${uploadAs}` : ''}`, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      // Attaching a receipt answers the expense_no_receipt check, so the review
      // is stale the moment this succeeds.
      void queryClient.invalidateQueries({ queryKey: ['review', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['expenses', org?.id] });
      // A receipt on a payment changes the count on the invoice's payment row.
      if (ownerType === 'payment') void queryClient.invalidateQueries({ queryKey: ['invoice-payments', org?.id] });
      // The register counts a month's EDF files.
      if (ownerType === 'export_declaration') void queryClient.invalidateQueries({ queryKey: ['exports', org?.id] });
    },
  });

  const remove = useMutation({
    mutationFn: (fileId: string) => api.delete(`/files/${fileId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ['review', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['expenses', org?.id] });
      if (ownerType === 'payment') void queryClient.invalidateQueries({ queryKey: ['invoice-payments', org?.id] });
      if (ownerType === 'export_declaration') void queryClient.invalidateQueries({ queryKey: ['exports', org?.id] });
    },
  });

  if (!perms.can('attachment', 'view')) return null;

  const canAdd = perms.can('attachment', 'create');
  const error = (upload.error ?? remove.error) instanceof ApiError
    ? ((upload.error ?? remove.error) as ApiError)
    : undefined;

  const send = (files: FileList | File[] | null) => {
    const first = files?.[0];
    // One at a time. A queue looks tidier and hides which of five files was the
    // one that was too large.
    if (first) upload.mutate(first);
  };

  const items = sourceFirst(query.data?.items ?? []);

  return (
    <div
      // A paste anywhere in this region counts, so the user does not have to
      // find and click a particular box first.
      onPaste={(e) => {
        if (!canAdd) return;
        const files = Array.from(e.clipboardData.files);
        if (files.length) {
          e.preventDefault();
          send(files);
        }
      }}
      onDragOver={(e) => {
        if (!canAdd) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (!canAdd) return;
        e.preventDefault();
        setDragging(false);
        send(e.dataTransfer.files);
      }}
      className={[
        'rounded-md border border-dashed p-3 transition-colors',
        dragging ? 'border-primary bg-primary/5' : 'border-border',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <Paperclip className="size-3.5" aria-hidden />
          {title}
        </p>
        {canAdd ? (
          <>
            <input
              ref={inputRef}
              type="file"
              className="sr-only"
              aria-label={uploadAs === 'source' ? 'Attach the original as the source' : `Attach a file to this ${ownerType.replace('_', ' ')}`}
              accept="image/jpeg,image/png,image/webp,image/heic,image/gif,application/pdf"
              onChange={(e) => {
                send(e.target.files);
                // Cleared so choosing the same file twice fires onChange again.
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={upload.isPending}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="size-3.5" aria-hidden />
              {uploadAs === 'source' ? 'Attach the original' : 'Attach'}
            </Button>
          </>
        ) : null}
      </div>

      {error ? (
        <p role="alert" className="mt-2 rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
          {error.message}
        </p>
      ) : null}

      {items.length ? (
        <ul className="mt-3 space-y-1.5">
          {items.map((file) => (
            <li
              key={file.id}
              className="flex items-center gap-2.5 rounded-md bg-muted/40 px-2 py-1.5 text-sm"
              data-testid="attachment-row"
              data-role={file.role ?? undefined}
            >
              {file.isImage ? (
                // The thumbnail is the same authenticated route as the
                // download, so it is subject to the same org check - there is
                // no second, laxer path to the bytes.
                <img
                  src={`/v1/files/${org?.id}/${file.id}`}
                  alt=""
                  className="size-9 shrink-0 rounded object-cover"
                  loading="lazy"
                />
              ) : (
                <span className="flex size-9 shrink-0 items-center justify-center rounded bg-background">
                  <FileText className="size-4 text-muted-foreground" aria-hidden />
                </span>
              )}

              <span className="min-w-0 flex-1">
                <a
                  href={`/v1/files/${org?.id}/${file.id}`}
                  download={file.filename}
                  className="block truncate font-medium text-primary hover:underline"
                >
                  {file.filename}
                </a>
                <span className="text-xs text-muted-foreground">
                  {file.role === 'source' ? (
                    /* The role, said in words and marked with the clip, so
                       the original an imported record was made from is told
                       apart from the receipt attached afterwards. */
                    <span className="mr-1.5 inline-flex items-center gap-1 font-medium text-foreground" data-testid="attachment-role">
                      <span aria-hidden>📎</span>
                      Source ·
                    </span>
                  ) : null}
                  {formatBytes(file.sizeBytes)}
                  {file.savedPercent > 0 ? (
                    /* Shown because it is a measured fact, not a promise: both
                       sizes are stored. It also quietly teaches people that
                       uploading the full-resolution photo is fine. */
                    <> · {file.savedPercent}% smaller than the original</>
                  ) : null}
                </span>
              </span>

              {perms.can('attachment', 'delete') ? (
                <button
                  type="button"
                  aria-label={`Remove ${file.filename}`}
                  onClick={() => remove.mutate(file.id)}
                  className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="size-3.5" aria-hidden />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">
          {emptyHint ?? 'Drag a file here, paste a screenshot, or use Attach. Images are compressed automatically.'}
        </p>
      )}
    </div>
  );
}
